/**
 * Friend overlay colors — 24 distinct hues, all chosen to avoid the
 * canonical PREFERRED-emerald (#059669) / FEASIBLE-blue (#2563eb) fills.
 *
 * The first 8 are the "default" palette shown directly in the color
 * picker. The next 16 are the "extended" palette revealed by a (+)
 * toggle. Auto-assignment prefers the default 8 — only spilling into
 * the extended set when all 8 default slots are already taken by other
 * active overlays.
 *
 * Determinism: assignment hashes ownerId → idx and resolves collisions
 * by linear-probing through the default palette, then the extended
 * palette. Sorted-input order makes the resolution stable across
 * reloads of the same poll, while the hash on ownerId means a given
 * friend gets the same color across different polls (unless the
 * viewer has explicitly overridden it via the picker).
 */

// First 8 — shown by default in the picker.
const OVERLAY_PALETTE_DEFAULT = Object.freeze([
  "#f472b6", // pink-400
  "#fb7185", // rose-400
  "#e879f9", // fuchsia-400
  "#a78bfa", // violet-400
  "#facc15", // yellow-400
  "#fb923c", // orange-400
  "#f87171", // red-400
  "#94a3b8", // slate-400
]);

// Next 16 — revealed by (+) in the picker. Mix of deeper + lighter
// variants to give visual diversity beyond the default 8.
const OVERLAY_PALETTE_EXTENDED = Object.freeze([
  "#ec4899", // pink-500
  "#f43f5e", // rose-500
  "#d946ef", // fuchsia-500
  "#a855f7", // purple-500
  "#eab308", // yellow-500
  "#f97316", // orange-500
  "#ef4444", // red-500
  "#64748b", // slate-500
  "#f9a8d4", // pink-300
  "#fda4af", // rose-300
  "#f0abfc", // fuchsia-300
  "#c4b5fd", // violet-300
  "#fde047", // yellow-300
  "#fdba74", // orange-300
  "#fca5a5", // red-300
  "#cbd5e1", // slate-300
]);

const OVERLAY_PALETTE_FULL = Object.freeze([
  ...OVERLAY_PALETTE_DEFAULT,
  ...OVERLAY_PALETTE_EXTENDED,
]);

export {
  OVERLAY_PALETTE_DEFAULT,
  OVERLAY_PALETTE_EXTENDED,
  OVERLAY_PALETTE_FULL,
};

function djb2(input) {
  // Simple deterministic 32-bit hash. Same algorithm + palette length
  // means the same uid → same default color across runs and clients.
  let h = 5381;
  for (let i = 0; i < input.length; i += 1) {
    h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

/**
 * Hash-based default color for an owner — always pulled from the
 * default 8-color palette. Returns null for falsy/non-string input
 * so callers can skip styling for the viewer's own marks.
 *
 * Note: this is the *unconstrained* mapping. For per-poll assignment
 * with collision avoidance against other active overlays, use
 * `assignOverlayColor` (or `resolveOwnerColor` for the full
 * override-aware path).
 */
export function ownerTintColor(ownerId) {
  if (typeof ownerId !== "string" || !ownerId) return null;
  const idx = djb2(ownerId) % OVERLAY_PALETTE_DEFAULT.length;
  return OVERLAY_PALETTE_DEFAULT[idx];
}

/**
 * Assign a deterministic, collision-aware color for an overlay owner.
 *
 *   1. Hash ownerId → idx in the default 8-color palette.
 *   2. If that color is already in `takenColors`, linear-probe forward
 *      through the default palette until a free slot is found.
 *   3. If all 8 default slots are taken, fall through to the extended
 *      16-color palette (starting at hash mod 16) and probe forward.
 *   4. If all 24 are taken (>=24 active overlays), wrap around — every
 *      color is reused but the algorithm still produces a stable result.
 *
 * Caller is responsible for sorting the input list so the assignment
 * order is deterministic across reloads. Self-overlay (no glow) should
 * never be passed in.
 */
export function assignOverlayColor(ownerId, takenColors = []) {
  if (typeof ownerId !== "string" || !ownerId) return null;
  const taken = new Set(takenColors);

  const defaultStart = djb2(ownerId) % OVERLAY_PALETTE_DEFAULT.length;
  for (let i = 0; i < OVERLAY_PALETTE_DEFAULT.length; i += 1) {
    const idx = (defaultStart + i) % OVERLAY_PALETTE_DEFAULT.length;
    const c = OVERLAY_PALETTE_DEFAULT[idx];
    if (!taken.has(c)) return c;
  }

  const extendedStart = djb2(ownerId) % OVERLAY_PALETTE_EXTENDED.length;
  for (let i = 0; i < OVERLAY_PALETTE_EXTENDED.length; i += 1) {
    const idx = (extendedStart + i) % OVERLAY_PALETTE_EXTENDED.length;
    const c = OVERLAY_PALETTE_EXTENDED[idx];
    if (!taken.has(c)) return c;
  }

  // All 24 taken — wrap. Stable but reused.
  return OVERLAY_PALETTE_DEFAULT[defaultStart];
}

/**
 * Resolve an owner's color across an active overlay set, honoring
 * per-viewer per-friend overrides from localStorage.
 *
 * Inputs:
 *   ownerId               — uid of the friend to resolve
 *   activeOwnerIdsInOrder — full list of active overlay owner uids,
 *                           sorted (typically alphabetical by displayName,
 *                           with ties broken by uid). Must include `ownerId`.
 *   overridesByOwnerId    — map<ownerId, hex|null>; values from
 *                           overlay-color-overrides.js
 *
 * Algorithm: walk the sorted list in order. For each entry:
 *   - if there's an override, claim that color (added to takenColors)
 *     even if it collides with an auto-assigned one earlier in the walk.
 *   - else, run `assignOverlayColor` against the running `takenColors`.
 *
 * Returns the color for `ownerId`, or null if `ownerId` isn't in the list.
 *
 * Note on override + collision: if user A is auto-assigned pink, then
 * user B (later in the sort) overrides to pink, both will end up pink.
 * This is intentional — explicit overrides win, and color-clashes are
 * the user's prerogative. Ordering means earlier-sorted users keep
 * their auto-assignment when a later user overrides into the same hue.
 */
export function resolveOwnerColor(
  ownerId,
  activeOwnerIdsInOrder = [],
  overridesByOwnerId = {},
) {
  if (typeof ownerId !== "string" || !ownerId) return null;
  if (!Array.isArray(activeOwnerIdsInOrder)) return null;

  const taken = [];
  let result = null;
  for (const uid of activeOwnerIdsInOrder) {
    const override =
      overridesByOwnerId && typeof overridesByOwnerId[uid] === "string"
        ? overridesByOwnerId[uid]
        : null;
    let assigned;
    if (override) {
      assigned = override;
    } else {
      assigned = assignOverlayColor(uid, taken);
    }
    taken.push(assigned);
    if (uid === ownerId) {
      result = assigned;
    }
  }
  return result;
}

/**
 * Build the eventPropGetter style chunk for a single overlay event.
 * Returns `{}` when the event has no ownerId (i.e. own-mark or
 * non-overlay) or when an explicit color isn't supplied.
 *
 * Two call shapes:
 *   1. ownerTintStyle(event) — legacy; uses `ownerTintColor` (no
 *      collision-avoidance, no override).
 *   2. ownerTintStyle(event, color) — caller has already resolved the
 *      color (e.g. via resolveOwnerColor) and just wants the style.
 *
 * Shape mirrors the existing scheduler eventPropGetter contract:
 * spread the result into the caller's style object.
 */
export function ownerTintStyle(event, color) {
  const ownerId = event?.resource?.ownerId;
  if (!ownerId) return {};
  const resolved =
    typeof color === "string" && color ? color : ownerTintColor(ownerId);
  if (!resolved) return {};
  return {
    borderColor: resolved,
    borderWidth: 2,
    borderStyle: "solid",
  };
}
